import { createPortal } from 'react-dom';
import { useHeaderActionsTarget } from '@/layouts/usePageTitle';
import { useEffect, useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllEnterpriseModule,
  colorSchemeLightCold,

  themeQuartz,
} from 'ag-grid-enterprise';
import type { ColDef } from 'ag-grid-community';
import { fetchTaskCreateLookups, fetchTasks, type TaskCreateLookups, type TaskListItem } from '@/api/tasks';
import { useI18n } from '@/i18n';
import { useAuthStore } from '@/store/authStore';
import TaskCreateModal from './TaskCreateModal';
import { TaskBoardContent, TaskBoardDrawer, TaskCategoryFilterDropdown } from './TaskBoardPage';
import type { TaskCreateRouteState } from './TaskCreatePage';
import { useErrorToast, LoadRetry } from '@/lib/notify';

const enterpriseModules = [AllEnterpriseModule];

const theme = themeQuartz.withPart(colorSchemeLightCold).withParams({
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 11.5,
  rowHeight: 32,
  headerHeight: 32,
  borderRadius: 6,
  headerBackgroundColor: '#f8fafc',
  borderColor: '#e2e8f0',
  oddRowBackgroundColor: '#f8fafc',
});

function statusLabel(status: number, t: (key: string) => string): string {
  if (status === 1) return t('tasks.status.todo');
  if (status === 2) return t('tasks.status.inProgress');
  if (status === 3) return t('tasks.status.done');
  if (status === 4) return t('tasks.status.canceled');
  if (status === 5) return t('tasks.status.closed');
  return String(status);
}

function StatusBadge({ status }: { status: number }) {
  const { t } = useI18n();
  const cls =
    status === 3
      ? 'bg-emerald-100 text-emerald-700'
      : status === 4
        ? 'bg-rose-100 text-rose-700'
        : status === 5
          ? 'bg-slate-200 text-slate-700'
        : status === 2
          ? 'bg-amber-100 text-amber-700'
          : 'bg-slate-100 text-slate-700';

  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>
      {statusLabel(status, t)}
    </span>
  );
}

export default function TasksPage() {
  const headerActionsTarget = useHeaderActionsTarget();
  const { t } = useI18n();
  const currentUser = useAuthStore((s) => s.user);
  const [rows, setRows] = useState<TaskListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useErrorToast(error);
  const [createOpen, setCreateOpen] = useState(false);
  const [createState, setCreateState] = useState<TaskCreateRouteState | null>(null);
  const [activeUuid, setActiveUuid] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'kanban' | 'classic'>('kanban');
  const [filter, setFilter] = useState<'all' | 'mine'>('all');
  const [lookups, setLookups] = useState<TaskCreateLookups | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');

  function loadTasks() {
    setLoading(true);
    setError(null);
    Promise.all([
      fetchTasks(),
      fetchTaskCreateLookups().catch(() => null),
    ])
      .then(([taskRows, lookupData]) => {
        setRows(taskRows);
        setLookups(lookupData);
      })
      .catch(() => setError(t('tasks.loadError')))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadTasks();
  }, [t]);

  const filteredRows = useMemo(
    () => rows.filter((row) => {
      if (categoryFilter && String(row.task_category_id) !== categoryFilter) return false;
      if (filter !== 'mine' || !currentUser) return true;
      return (row.assignees ?? []).some((assignee) => assignee.id === currentUser.id);
    }),
    [categoryFilter, currentUser, filter, rows],
  );

  const colDefs = useMemo<ColDef<TaskListItem>[]>(
    () => [
      { field: 'id', headerName: t('tasks.id'), width: 90, filter: 'agNumberColumnFilter', type: 'numericColumn', valueFormatter: ({ value }) => value == null ? '' : Number(value).toLocaleString('tr-TR', { maximumFractionDigits: 0 }) },
      { field: 'title', headerName: t('tasks.titleField'), flex: 1.3, minWidth: 220 },
      { field: 'power_plant_name', headerName: t('tasks.powerPlant'), flex: 1.4, minWidth: 180 },
      { field: 'task_category_name', headerName: t('tasks.category'), flex: 1.1, minWidth: 150 },
      {
        field: 'priority',
        headerName: t('tasks.priority'),
        width: 100,
        filter: 'agSetColumnFilter',
        valueFormatter: ({ value }: { value: number }) =>
          value === 1 ? t('tasks.priorityLabel.low')
            : value === 2 ? t('tasks.priorityLabel.middle')
            : value === 3 ? t('tasks.priorityLabel.high')
            : String(value ?? ''),
      },
      { field: 'description', headerName: t('tasks.description'), flex: 2, minWidth: 240 },
      { field: 'task_created_date', headerName: t('tasks.created'), flex: 1.2, minWidth: 150 },
      { field: 'task_due_date', headerName: t('tasks.due'), flex: 1, minWidth: 120 },
      {
        field: 'task_status',
        headerName: t('common.status'),
        width: 130,
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: ({ value }: { value: number }) => statusLabel(Number(value), t),
        },
        cellRenderer: ({ value }: { value: number }) => <StatusBadge status={value} />,
      },
    ],
    [t],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: false,
      suppressHeaderMenuButton: false,
    }),
    [],
  );

  if (error) return <LoadRetry />;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {headerActionsTarget && createPortal(<div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
            <label className="flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 text-slate-400">
              <Search size={14} />
              <input type="search" aria-label="Görev listesinde ara" placeholder="Görevlerde ara…" value={search} onChange={(event) => setSearch(event.target.value)} className="w-32 bg-transparent text-xs text-slate-700 outline-none" />
            </label>
          <div className="flex h-8 rounded-lg border border-slate-200 bg-white p-0.5">
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
                filter === 'all' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {t('tasks.filters.all')}
            </button>
            <button
              type="button"
              onClick={() => setFilter('mine')}
              className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
                filter === 'mine' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {t('tasks.filters.mine')}
            </button>
          </div>
          <div className="flex h-8 rounded-lg border border-slate-200 bg-white p-0.5">
            <button
              type="button"
              onClick={() => setActiveTab('kanban')}
              className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
                activeTab === 'kanban' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {t('tasks.tabs.kanban')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('classic')}
              className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
                activeTab === 'classic' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {t('tasks.tabs.classic')}
            </button>
          </div>
          <TaskCategoryFilterDropdown
            compact
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={lookups?.taskcategories ?? []}
            label={t('tasks.filters.allCategories')}
          />
        </div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="flex items-center justify-center gap-1.5 rounded-lg h-8 bg-blue-600 px-3 text-xs font-medium text-white transition hover:bg-blue-700"
          >
            <Plus size={14} />
            {activeTab === 'classic' ? 'Yeni' : t('tasks.new')}
          </button>
      </div>,headerActionsTarget)}
      <section aria-label="Görev listesi" className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
      {activeTab === 'kanban' ? (
        <TaskBoardContent
          embedded
          search={search}
          filter={filter}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          categoryOptions={lookups?.taskcategories ?? []}
        />
      ) : (
        <div className="min-h-0 flex-1 [&_.ag-paging-row-summary-panel]:hidden">
          <AgGridReact<TaskListItem>
            modules={enterpriseModules}
            theme={theme}
            rowData={loading ? null : filteredRows}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            quickFilterText={search}
            getRowId={(params) => params.data.uuid_id}
            pagination
            paginationPageSize={100}
            paginationPageSizeSelector={[25, 50, 100]}
            animateRows
            rowStyle={{ cursor: 'pointer' }}
            onCellClicked={(e) => {
              if (e.data?.uuid_id) {
                setActiveUuid(e.data.uuid_id);
              }
            }}
            enableCellTextSelection
          />
        </div>
      )}
      </section>

      <TaskCreateModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreateState(null);
        }}
        onCreated={loadTasks}
        initialValues={createState?.initialValues}
        mode={createState?.mode}
        sourceTaskUuidId={createState?.sourceTaskUuidId}
        sourceTaskLabel={createState?.sourceTaskLabel}
      />

      {activeUuid && (
        <TaskBoardDrawer
          uuid={activeUuid}
          onClose={() => setActiveUuid(null)}
          onRefresh={loadTasks}
          onOpenCreate={(state) => {
            setActiveUuid(null);
            setCreateState(state);
            setCreateOpen(true);
          }}
        />
      )}
    </div>
  );
}
